import { useScrollReveal } from '@/hooks/useScrollReveal';

const fadeIn = (visible: boolean, delay: string) => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(24px)',
  transition: 'all 700ms ease-out',
  transitionDelay: delay,
});

export default function StatsSection() {
  const [leftRef, leftVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.12 });
  const [rightRef, rightVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.12 });

  return (
    <section className="relative py-20 md:py-28 px-4 md:px-8 lg:px-12 overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background-100/95 via-background-50/95 to-background-100/95" />

      <div className="relative max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left — Text */}
          <div ref={leftRef} className="lg:w-1/2">
            <span
              className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3"
              style={fadeIn(leftVisible, '0ms')}
            >
              / Global Presence
            </span>
            <h2
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-50 leading-tight mb-6"
              style={fadeIn(leftVisible, '150ms')}
            >
              Trusted Across
              <br />
              <span className="text-primary-400">the Globe</span>
            </h2>
            <p
              className="text-foreground-400 text-sm md:text-base font-body leading-relaxed max-w-xl"
              style={fadeIn(leftVisible, '300ms')}
            >
              TMT delivers the infrastructure that keeps digital payments flowing — securely, reliably, and at scale. Our always-on network architecture ensures your transactions never sleep and your business never waits. Wherever your money needs to go, our network is always on.
            </p>
          </div>

          {/* Right — Photo + Badges */}
          <div ref={rightRef} className="lg:w-1/2 flex flex-col gap-4">
            {/* Photo */}
            <div
              className="relative overflow-hidden rounded-xl border border-primary-700/20 group cursor-default"
              style={fadeIn(rightVisible, '100ms')}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 via-transparent to-transparent z-10" />
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20fintech%20global%20operations%20center%20with%20world%20map%20hologram%20displays%20professional%20team%20monitoring%20digital%20payment%20networks%20across%20multiple%20continents%20sleek%20corporate%20environment%20with%20warm%20golden%20accent%20lighting%20premium%20financial%20technology%20workspace&width=1200&height=600&seq=global-ops-fin-1&orientation=landscape"
                alt="TMT Global Operations — Fintech Network Monitoring Center"
                className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>

            {/* Badges Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div
                className="flex flex-col items-center justify-center gap-3 p-4 md:p-5 bg-background-100/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-300 cursor-default"
                style={fadeIn(rightVisible, '250ms')}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                  <i className="ri-global-line text-primary-400 text-2xl md:text-3xl" />
                </div>
                <span className="text-foreground-200 text-sm md:text-base font-label tracking-wide uppercase whitespace-nowrap font-semibold text-center">14+ Corridors</span>
              </div>
              <div
                className="flex flex-col items-center justify-center gap-3 p-4 md:p-5 bg-background-100/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-300 cursor-default"
                style={fadeIn(rightVisible, '350ms')}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                  <i className="ri-cloud-line text-primary-400 text-2xl md:text-3xl" />
                </div>
                <span className="text-foreground-200 text-sm md:text-base font-label tracking-wide uppercase whitespace-nowrap font-semibold text-center">99.9% Uptime</span>
              </div>
              <div
                className="flex flex-col items-center justify-center gap-3 p-4 md:p-5 bg-background-100/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-300 cursor-default"
                style={fadeIn(rightVisible, '450ms')}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                  <i className="ri-shield-check-line text-primary-400 text-2xl md:text-3xl" />
                </div>
                <span className="text-foreground-200 text-sm md:text-base font-label tracking-wide uppercase whitespace-nowrap font-semibold text-center">256-bit Security</span>
              </div>
              <div
                className="flex flex-col items-center justify-center gap-3 p-4 md:p-5 bg-background-100/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-300 cursor-default"
                style={fadeIn(rightVisible, '550ms')}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0">
                  <i className="ri-24-hours-line text-primary-400 text-2xl md:text-3xl" />
                </div>
                <span className="text-foreground-200 text-sm md:text-base font-label tracking-wide uppercase whitespace-nowrap font-semibold text-center">24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}